import React from "react";
import ThreeRowTiltedMarquee from "../components/ThreeRowTiltedMarquee";
import FaqList from "../components/FaqList";
import { Link } from "react-router-dom";

const Scanlines = () => (
    <div
        className="absolute inset-0 pointer-events-none mix-blend-soft-light"
        style={{
            backgroundImage:
                "repeating-linear-gradient(to bottom, rgba(255,255,255,.03), rgba(255,255,255,.03) 1px, transparent 1px, transparent 3px)",
        }}
    />
);

export default function Home() {

    return (
        <>
            {/* ===== Hero ===== */}
            <section className="relative grid place-items-center min-h-[90vh] overflow-hidden" aria-label="Intro">
                {/* Tilted marquee background */}
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <ThreeRowTiltedMarquee
                        height="100%"
                        background="transparent"
                        rowOverlap={-40}
                        row1={{ speed: 70, tilt: 28, roll: 2, direction: "left" }}
                        row2={{ speed: 92, tilt: -28, roll: 3, direction: "right" }}
                        row3={{ speed: 62, tilt: 28, roll: 4, direction: "left" }}
                    />
                    {/* overlays to match your vibe */}
                    <Scanlines />
                    <div className="absolute inset-0 -m-1 bg-[radial-gradient(1200px_480px_at_50%_50%,transparent_55%,rgba(0,0,0,.65)_80%),linear-gradient(180deg,rgba(0,0,0,.5),rgba(0,0,0,.85))]" />
                </div>

                {/* Foreground hero content */}
                <div className="container-x relative text-center py-24 md:py-20">
                    <p className="uppercase tracking-[0.2em] text-[#D4FF00] font-black text-xs mb-3">
                        Revelstoke • British Columbia
                    </p>
                    <h1 className="text-4xl md:text-[51.2px] lg:text-6xl font-extrabold leading-[1.05]">
                        Level up. <span className="whitespace-nowrap">Play on.</span><br />
                        <span className="bg-[#D4FF00] text-black rounded px-1 shadow-[0_0_0_6px_rgba(212,255,0,.15)]">
                            Book your rig.
                        </span>{" "}
                        Game together.
                    </h1>
                    <div className="flex flex-wrap justify-center gap-3 mt-10">
                        <Link to="/booking" className="btn btn-accent px-4 py-2 text-sm font-550 hover:bg-black hover:text-white transition:hover duration-150 ease-linear">Book Now</Link>
                        <Link to="/pricing" className="btn px-4 bg-white text-sm font-550 text-black border border-[#1a1a1a] hover:bg-black hover:text-white transition:hover duration-150 ease-linear">Pricing</Link>
                    </div>
                </div>
            </section>


            {/* ===== Statement ===== */}
            <section className="py-9">
                <div className="container-x max-w-[900px] text-center md:text-left py-8 md:py-20">
                    <h2 className="text-2xl md:text-4xl lg:text-4xl font-extrabold text-left">Top-tier gaming. Unrivaled amenities.</h2>
                    <p className="text-[#D4FF00] font-extrabold text-2xl md:text-4xl lg:text-4xl inline-block mt-2 text-left">
                        Every visit delivers a next-gen<br className="hidden md:block" />
                        experience, from hardware to hospitality.
                    </p>
                </div>
            </section>

            {/* ===== Features (3 cards) ===== */}
            <section id="features" className="py-8 md:py-20 max-w-full mx-auto">
                <div className="container-x grid gap-10 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 my-12 w-full">
                    {[
                        {
                            img: "/images/kidsplayingesports.avif",
                            title: "Esports Ready Stations 🎮",
                            text: "Our high-spec PCs and peripherals are perfect for tournaments, training, or clutch plays with your squad.",
                        },
                        {
                            img: "/images/chillzone.avif",
                            title: "Chill Zone & Lounge 👥",
                            text: "Recharge between matches in our cozy lounge with bean bags, board games, and drinks. Designed for comfort, made for gamers.",
                        },
                        {
                            img: "/images/vrzone.avif",
                            title: "Immersive Ambience ⚡",
                            text: "Designed for immersion dynamic LED setups and futuristic interiors create the ultimate gaming environment.",
                        },
                    ].map(({ img, title, text }) => (
                        <article key={title}>
                            <img src={img} alt={title} className="w-full h-[300px] object-cover block" />
                            <div className="p-4 text-neutral-400">
                                <h3 className="text-neutral-100 text-center font-extrabold text-xl lg:text-2xl mb-2">{title}</h3>
                                <p className="text-sm text-white text-center font-semibold leading-relaxed">{text}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* ===== Icon row ===== */}
            <section id="icons" className="py-8 lg:my-10">
                <div className="container-x grid gap-5 grid-cols-2 md:grid-cols-4 text-center lg:my-8">
                    {[
                        {
                            label: "Fiber Internet",
                            icon: (
                                <svg viewBox="0 0 24 24" className="w-14 h-14 stroke-[#D4FF00] fill-none stroke-2">
                                    {/* big arc */}
                                    <path d="M3 10.5C6.5 7 17.5 7 21 10.5" strokeLinecap="round" />
                                    {/* mid arc */}
                                    <path d="M6 13c3-3 9-3 12 0" strokeLinecap="round" />
                                    {/* small arc */}
                                    <path d="M9 15.5c1.8-1.8 4.2-1.8 6 0" strokeLinecap="round" />
                                    {/* dot */}
                                    <circle cx="12" cy="18" r="1.5" />
                                </svg>
                            ),
                        },
                        {
                            label: "240Hz Monitors",
                            icon: (
                                <svg viewBox="0 0 24 24" className="w-14 h-14 stroke-[#D4FF00] fill-none stroke-2">
                                    <rect x="3" y="5" width="18" height="12" rx="2" ry="2" />
                                    <path d="M8 21h8" />
                                    <path d="M12 17v4" />
                                </svg>
                            ),
                        },
                        {
                            label: "Pro Peripherals",
                            icon: (
                                <svg viewBox="0 0 24 24" className="w-14 h-14 stroke-[#D4FF00] fill-none stroke-2">
                                    <rect x="3" y="7" width="18" height="10" rx="2" />
                                    <path d="M7 10h10" strokeLinecap="round" />
                                    <path d="M7 13h7" strokeLinecap="round" />
                                </svg>
                            ),
                        },
                        {
                            label: "Console & PC Bays",
                            icon: (
                                <svg viewBox="0 0 24 24" className="w-14 h-14 stroke-[#D4FF00] fill-none stroke-2">
                                    {/* bridge/top */}
                                    <path d="M6 15l2-2h8l2 2" strokeLinecap="round" />
                                    {/* left handle */}
                                    <path d="M6 15c-2 0-3 1.5-3 3s1 3 3 3h2" strokeLinecap="round" />
                                    {/* right handle */}
                                    <path d="M18 15c2 0 3 1.5 3 3s-1 3-3 3h-2" strokeLinecap="round" />
                                    {/* d-pad */}
                                    <path d="M8 12v2M7 13h2" strokeLinecap="round" />
                                    {/* AB buttons */}
                                    <circle cx="16.5" cy="13" r="1" />
                                    <circle cx="18.5" cy="11" r="1" />
                                </svg>
                            ),
                        },
                    ].map(({ label, icon }) => (
                        <div key={label} className="flex flex-col items-center gap-2">
                            {icon}
                            <p className="font-semibold text-xl lg:text-2xl">{label}</p>
                        </div>
                    ))}
                </div>
            </section>


            {/* ===== Testimonial ===== */}
            <section className="py-10 my-16">
                <div className="container-x max-w-[900px] text-center">
                    <blockquote className="rounded-2xl p-6 ">
                        <p className="text-xl md:text-3xl lg:text-3xl font-semibold">
                            “Revelstoke Gaming Cafe is the best! Fast PCs, comfy chairs, and the atmosphere is unmatched—Highly recommend for any gamer.”
                        </p>
                        <cite className="block mt-2 text-accent not-italic">— Micah L.</cite>
                    </blockquote>
                </div>
            </section>

            {/* ===== FAQ ===== */}
            <section id="faq" className="py-8">
                <div className="container-x max-w-[760px]">
                    <h2 className="text-center text-xl font-bold mb-3">FAQ</h2>

                    <FaqList
                        singleOpen
                        defaultOpenIndex={0}
                        items={[
                            ["How do I book a station?", "Book online with the green button or call us. Walk-ins are welcome if seats are open."],
                            ["Do you support console gaming?", "Yes—Xbox/PlayStation bays are available. Bring your account; we’ll get you signed in."],
                            ["Can I bring my own peripherals?", "Absolutely. We provide premium keyboards, mice, and headsets, but BYO is encouraged."],
                            ["Minimum age & supervision?", "Under 12 must be accompanied by an adult/guardian in the venue."],
                            ["Snacks & drinks policy?", "Café items only at desks (to protect gear). Lounge tables are available for outside food."],
                        ]}
                    />
                </div>
            </section>

            {/* anchor for CTA */}
            <section id="book" className="py-2" aria-hidden="true" />
        </>
    );
}
